import {
  AppWindow,
  Bot,
  Globe,
  LayoutTemplate,
  MousePointerClick,
  Smartphone,
} from "lucide-react";

export type Language = "he" | "en";

export type Direction = "rtl" | "ltr";

export const languageMeta: Record<
  Language,
  {
    dir: Direction;
    label: string;
    shortLabel: string;
    switchLabel: string;
    ariaLabel: string;
  }
> = {
  he: {
    dir: "rtl",
    label: "עברית",
    shortLabel: "HE",
    switchLabel: "EN",
    ariaLabel: "Switch to English",
  },
  en: {
    dir: "ltr",
    label: "English",
    shortLabel: "EN",
    switchLabel: "עב",
    ariaLabel: "החלף לעברית",
  },
};

export const translations = {
  he: {
    documentTitle: "סוכנות פרפורמנס",
    nav: {
      caseStudies: "קייסים",
      bookCall: "קבעו שיחה",
      links: [
        { href: "#home", label: "בית" },
        { href: "#about", label: "עלינו" },
        { href: "#services", label: "שירותים" },
      ],
    },
    hero: {
      headline: "אנחנו CTR\u200e נעים להכיר",
      subtitle:
        "אנחנו מתמחים בניהול קמפיינים ניתוח נתונים ופיתוח אתרים ואפליקציות כבר 13 שנים ",
      cta: "אני רוצה לדעת עוד",
    },
    spline: {
      title: "אז מי אנחנו ",
      description:
        "13 שנות ניסיון בניהול קמפיינים ופיתוח Web לחברות מובילות בארץ ובחו״ל  אסטרטגיה, דאטה, קריאייטיב וטכנולוגיה שמייצרים תוצאות.",
    },
    timeline: {
      heading: "אקוסיסטם הצמיחה שלך",
      subheading: "לחצו על כל שירות וגלו איך הם מתחברים יחד",
      status: {
        completed: "שירות פעיל",
        inProgress: "מומלץ לשילוב",
        pending: "לפי צורך",
      },
      energyLevel: "התאמה לעסק",
      connectedNodes: "משתלב מצוין עם",
      items: [
        {
          id: 1,
          title: "פיתוח אתרים",
          date: "שירות 01",
          content:
            "אתר תדמית מהיר, חד וברור שמציג את העסק בצורה מקצועית, בונה אמון ומוביל את הגולש לפעולה. כולל מבנה נכון, עיצוב רספונסיבי וחוויית משתמש שמתאימה למובייל ולדסקטופ.",
          category: "אתרים",
          icon: Globe,
          relatedIds: [2, 4],
          status: "completed",
          energy: 100,
        },
        {
          id: 2,
          title: "פיתוח אפליקציות Web",
          date: "שירות 02",
          content:
            "מערכות Web ואפליקציות דפדפן שמותאמות בדיוק לתהליך העבודה של העסק: אזורי משתמש, דאשבורדים, טפסים חכמים, ניהול מידע ואוטומציות שמחברות את כל החלקים במקום אחד.",
          category: "מערכות",
          icon: AppWindow,
          relatedIds: [1, 3, 6],
          status: "completed",
          energy: 95,
        },
        {
          id: 3,
          title: "ניהול קמפיינים PPC",
          date: "שירות 03",
          content:
            "קמפיינים ממומנים בגוגל וברשתות החברתיות עם חשיבה על תוצאה עסקית, לא רק קליקים. אנחנו בונים מבנה קמפיין, קהלים, מודעות, מעקב המרות ואופטימיזציה שוטפת כדי להוציא יותר מכל שקל.",
          category: "פרפורמנס",
          icon: MousePointerClick,
          relatedIds: [1, 4],
          status: "in-progress",
          energy: 90,
        },
        {
          id: 4,
          title: "דפי נחיתה",
          date: "שירות 04",
          content:
            "דפי נחיתה ממוקדים לקמפיינים, השקות ומבצעים. כל עמוד נבנה סביב מסר חד, היררכיית תוכן נכונה, מהירות טעינה וטופס שמקל על הלקוח להשאיר פרטים.",
          category: "המרות",
          icon: LayoutTemplate,
          relatedIds: [1, 3],
          status: "completed",
          energy: 88,
        },
        {
          id: 5,
          title: "פיתוח אפליקציות מובייל",
          date: "שירות 05",
          content:
            "אפליקציות מובייל לעסקים שצריכים חוויה זמינה בכיס של הלקוח או של הצוות. אנחנו מאפיינים את המסכים, בונים זרימה נוחה ומפתחים בסיס טכנולוגי שאפשר להרחיב בהמשך.",
          category: "מובייל",
          icon: Smartphone,
          relatedIds: [2, 6],
          status: "pending",
          energy: 82,
        },
        {
          id: 6,
          title: "אינטגרציות AI לעסקים",
          date: "שירות 06",
          content:
            "חיבור כלי AI לתהליכים אמיתיים בעסק: סיכום לידים, מענה ראשוני, אוטומציות שירות, הפקת תוכן, ניתוח נתונים וחיבור למערכות קיימות כדי לחסוך זמן ולשפר תגובה.",
          category: "AI",
          icon: Bot,
          relatedIds: [2, 5],
          status: "in-progress",
          energy: 86,
        },
      ],
    },
    floating: {
      title: "עולם של חדשנות",
      subtitle:
        "מחברים את הכלים, הטכנולוגיות והקריאייטיב שמקדמים מותגים קדימה.",
      ctaText: "בואו נבנה צמיחה",
      ctaHref: "#services",
    },
    servicesMap: {
      heading: "מה אנחנו עושים",
      tiles: [
        { title: "פיתוח אתרים", href: "https://ctrmedia.co.il/website/" },
        { title: "ניהול קמפיינים PPC", href: "https://ctrmedia.co.il/landing/" },
        { title: "פיתוח אינטגרציות AI לעסקים", href: "https://ctrmedia.co.il/AI/" },
        { title: "דפי נחיתה", href: "https://ctrmedia.co.il/website/" },
        { title: "פיתוח אפליקציות מובייל", href: "https://ctrmedia.co.il/website" },
        { title: "פיתוח אפליקציות Web", href: "https://ctrmedia.co.il/website" },
      ],
    },
  },
  en: {
    documentTitle: "Performance Agency",
    nav: {
      caseStudies: "Case Studies",
      bookCall: "Book a Call",
      links: [
        { href: "#home", label: "Home" },
        { href: "#about", label: "About" },
        { href: "#services", label: "Services" },
      ],
    },
    hero: {
      headline: "We're CTR — nice to meet you",
      subtitle:
        "We specialize in campaign management, data analysis, and building websites and apps.",
      cta: "I want to know more",
    },
    spline: {
      title: "Immersive Growth Systems",
      description:
        "Performance marketing with 11+ years’ experience: data-led paid campaigns, ad–site–CRM alignment, and custom sites and landing pages—built to measure, optimize, and grow revenue.",
    },
    timeline: {
      heading: "Your Growth Ecosystem",
      subheading: "Click any service to see how they connect",
      status: {
        completed: "Active Service",
        inProgress: "Recommended Pairing",
        pending: "As Needed",
      },
      energyLevel: "Business Fit",
      connectedNodes: "Works Well With",
      items: [
        {
          id: 1,
          title: "Website Development",
          date: "Service 01",
          content:
            "Fast, polished business websites that build trust and guide visitors toward action. Each site includes responsive design, clear structure and a user experience built for mobile and desktop.",
          category: "Websites",
          icon: Globe,
          relatedIds: [2, 4],
          status: "completed",
          energy: 100,
        },
        {
          id: 2,
          title: "Web App Development",
          date: "Service 02",
          content:
            "Custom browser-based systems for real business workflows: user areas, dashboards, smart forms, data management and automations that keep everything connected.",
          category: "Systems",
          icon: AppWindow,
          relatedIds: [1, 3, 6],
          status: "completed",
          energy: 95,
        },
        {
          id: 3,
          title: "PPC Campaign Management",
          date: "Service 03",
          content:
            "Paid campaigns on Google and social platforms focused on business outcomes, not just clicks. We build the structure, audiences, ads, conversion tracking and ongoing optimization.",
          category: "Performance",
          icon: MousePointerClick,
          relatedIds: [1, 4],
          status: "in-progress",
          energy: 90,
        },
        {
          id: 4,
          title: "Landing Pages",
          date: "Service 04",
          content:
            "Focused landing pages for campaigns, launches and offers. Every page is built around sharp messaging, clean hierarchy, fast loading and a form that makes conversion easy.",
          category: "Conversions",
          icon: LayoutTemplate,
          relatedIds: [1, 3],
          status: "completed",
          energy: 88,
        },
        {
          id: 5,
          title: "Mobile App Development",
          date: "Service 05",
          content:
            "Mobile apps for businesses that need a smooth experience in the customer's or team's pocket. We plan the screens, shape the flow and build a foundation that can grow.",
          category: "Mobile",
          icon: Smartphone,
          relatedIds: [2, 6],
          status: "pending",
          energy: 82,
        },
        {
          id: 6,
          title: "AI Integrations for Businesses",
          date: "Service 06",
          content:
            "Practical AI connected to real business processes: lead summaries, first-response flows, service automation, content generation, data analysis and integrations with existing tools.",
          category: "AI",
          icon: Bot,
          relatedIds: [2, 5],
          status: "in-progress",
          energy: 86,
        },
      ],
    },
    floating: {
      title: "A World of Innovation",
      subtitle:
        "Explore the tools, technologies and creative systems that move brands forward.",
      ctaText: "Build Growth",
      ctaHref: "#services",
    },
    servicesMap: {
      heading: "What We Do",
      tiles: [
        { title: "Web Development", href: "#" },
        { title: "PPC Campaign Management", href: "#" },
        { title: "AI Integrations for Businesses", href: "#" },
        { title: "Landing Pages", href: "#" },
        { title: "Mobile App Development", href: "#" },
        { title: "Web App Development", href: "#" },
      ],
    },
  },
} as const;

export type AppCopy = (typeof translations)[Language];
